import { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { isValidPhoneNumber } from 'react-phone-number-input';
// @mui
import { Alert, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFPhonenumber, RHFTextField } from '../../../components/hook-form';
import { GoldBorderLoadingButton } from '../../../components/nav-section/vertical/style';
import useLocales from '../../../hooks/useLocales';
import { toPlayerId } from '../../../utils/formatString';
import { requestOtp } from '../../../utils/otp';
import { getRegisterErrorMessage } from './registerErrors';
import { IS_OTP_READY } from '../../../config';

// ----------------------------------------------------------------------


export default function RegisterPhoneNumber({ verification = {}, setVerification }) {
  const { translate } = useLocales();

  const [showPassword, setShowPassword] = useState(false);

  const RegisterPhoneNumberSchema = Yup.object().shape({
    fullName: Yup.string().required(translate('x_is_required', { x: translate('full_name') })),
    password: Yup.string().required(translate('x_is_required', { x: translate('password') })),
    phoneNumber: Yup.string()
      .required(translate('x_is_required', { x: translate('phone_number') }))
      .test('valid-phone-number', translate('invalid_x', { x: translate('phone_number') }), (value) =>
        value ? isValidPhoneNumber(value) : false
      ),
  });

  // Seeded from `verification` so stepping back here doesn't wipe what was already typed.
  const defaultValues = {
    fullName: verification?.fullName || '',
    password: verification?.password || '',
    phoneNumber: verification?.number || '',
  };

  const methods = useForm({
    resolver: yupResolver(RegisterPhoneNumberSchema),
    defaultValues,
  });

  const {
    setError,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const fullName = watch('fullName');

  const onSubmit = async (data) => {
    try {
      const accountDetails = {
        detailsSubmitted: true,
        number: data.phoneNumber,
        fullName: data.fullName.trim(),
        password: data.password,
      };

      if (!IS_OTP_READY) {
        setVerification((prev) => ({ ...prev, ...accountDetails, id: null, isVerified: false }));
        return;
      }

      const otpUuid = await requestOtp(data.phoneNumber, 'register');

      setVerification((prev) => ({ ...prev, ...accountDetails, id: otpUuid }));
    } catch (error) {
      console.error(error);
      setError('afterSubmit', { ...error, message: getRegisterErrorMessage(error, translate) });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="fullName" label={translate('full_name')} />

        <Typography style={{ fontSize: '.75rem', color: '#c9c9c9', fontWeight: 500 }}>
          {translate('full_name_is_your_id')}
          {fullName?.trim() ? ` — ${translate('your_id')}: ${toPlayerId(fullName)}` : ''}
        </Typography>

        <RHFTextField
          name="password"
          label={translate('password')}
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <RHFPhonenumber name="phoneNumber" label={translate(IS_OTP_READY ? 'verify_phonenumber' : 'phone_number')} />

        <GoldBorderLoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="outlined"
          loading={isSubmitting}
          sx={{ py: 0, height: '40px' }}
        >
          {translate(IS_OTP_READY ? 'send_otp' : 'next')}
        </GoldBorderLoadingButton>
      </Stack>
    </FormProvider>
  );
}

RegisterPhoneNumber.propTypes = {
  verification: PropTypes.object,
  setVerification: PropTypes.func,
};
